import React, { useState, useEffect, useRef } from "react";
import { usePermissions } from "../context/PermissionContext";
import { PERMISSIONS } from "../utils/permissions";
import { Alert } from "antd";

const DashboardPage: React.FC = () => {
  const [selectedDashboard, setSelectedDashboard] = useState("generalReport");
  const [isLoading, setIsLoading] = useState(true);
  const loadedDashboards = useRef<Set<string>>(new Set());
  const { hasPermission } = usePermissions();

  // Simple permission check for different dashboards
  const hasAccessTo = {
    generalReport: hasPermission(PERMISSIONS.ACCESS_DASHBOARD),
    periodicalSales: hasPermission(PERMISSIONS.ACCESS_SALES_ANALYSIS),
    productSales: hasPermission(PERMISSIONS.ACCESS_SALES_ANALYSIS),
    userSignups: hasPermission(PERMISSIONS.ACCESS_REPORT_ANALYSIS),
    userEngagements: hasPermission(PERMISSIONS.ACCESS_REPORT_ANALYSIS),
    orders: hasPermission(PERMISSIONS.ACCESS_ORDERS),
    vendors: hasPermission(PERMISSIONS.VIEW_VENDORS),
  };

  // Check if user has access to current dashboard
  const canAccessCurrentDashboard =
    hasAccessTo[selectedDashboard as keyof typeof hasAccessTo];

  // Track which dashboards have been loaded
  const handleIframeLoad = (dashboard: string) => {
    loadedDashboards.current.add(dashboard);
    if (dashboard === selectedDashboard) {
      setIsLoading(false);
    }
  };

  // Reset loading state when switching dashboards
  useEffect(() => {
    // Check if this dashboard has been loaded before
    if (loadedDashboards.current.has(selectedDashboard)) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [selectedDashboard]);

  const handleDashboardChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedDashboard(event.target.value);
  };

  // Dashboard URLs map for easy maintenance
  const dashboardUrls = {
    generalReport:
      "https://app.powerbi.com/view?r=eyJrIjoiNGUzYjIxMDctNTljZS00NjViLWFkNGUtMDIwYzljNGNlNjlkIiwidCI6IjRhYjZlNDRmLWI2ODItNGZjYS05MTJjLTM4ZmRjYTI1ZDIxOSIsImMiOjZ9",
    periodicalSales:
      "https://app.powerbi.com/view?r=eyJrIjoiNjQzZTdkYTQtMzQ1MC00YjI5LWJmNDUtMDg5ZmRiNTYxNDQ4IiwidCI6IjRhYjZlNDRmLWI2ODItNGZjYS05MTJjLTM4ZmRjYTI1ZDIxOSIsImMiOjZ9",
    productSales:
      "https://app.powerbi.com/view?r=eyJrIjoiYTM5N2U3ZTAtY2E4ZS00NmJmLTljOWMtMTU0NDVmNGFmZjc2IiwidCI6IjRhYjZlNDRmLWI2ODItNGZjYS05MTJjLTM4ZmRjYTI1ZDIxOSIsImMiOjZ9",
    userSignups:
      "https://app.powerbi.com/view?r=eyJrIjoiMGQ5MDlhZDAtNjM0NS00MjE5LWFhYzQtODhhNzZmMmVhYTk2IiwidCI6IjRhYjZlNDRmLWI2ODItNGZjYS05MTJjLTM4ZmRjYTI1ZDIxOSIsImMiOjZ9",
    userEngagements:
      "https://app.powerbi.com/view?r=eyJrIjoiNDFiZDAzMzUtZjhlYS00ZGE3LTlmZDgtNTI4N2QxMGYyZWQ4IiwidCI6IjRhYjZlNDRmLWI2ODItNGZjYS05MTJjLTM4ZmRjYTI1ZDIxOSIsImMiOjZ9",
    orders:
      "https://app.powerbi.com/view?r=eyJrIjoiNDllNjQ2OWQtNzJjZi00ZGVjLWE0ZmQtNmY3MmFkNGY4M2ZkIiwidCI6IjRhYjZlNDRmLWI2ODItNGZjYS05MTJjLTM4ZmRjYTI1ZDIxOSIsImMiOjZ9",
    vendors:
      "https://app.powerbi.com/view?r=eyJrIjoiMjg2YzNkNDQtNDIyMC00ZTAyLTg3NTAtMjljMzkxY2Y1NzI4IiwidCI6IjRhYjZlNDRmLWI2ODItNGZjYS05MTJjLTM4ZmRjYTI1ZDIxOSIsImMiOjZ9",
  };

  return (
    <div className="min-h-screen p-6 bg-white">
      {/* Header and Dropdown Side by Side */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <div className="w-full md:w-64">
          <label htmlFor="dashboard-select" className="sr-only">
            Select Dashboard
          </label>
          <select
            id="dashboard-select"
            value={selectedDashboard}
            onChange={handleDashboardChange}
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-secondary focus:border-secondary sm:text-sm rounded-md"
          >
            {/* Only show dashboards the user has permission to view */}
            {hasAccessTo.generalReport && (
              <option value="generalReport">General Report</option>
            )}
            {hasAccessTo.periodicalSales && (
              <option value="periodicalSales">Periodical Sales Analysis</option>
            )}
            {hasAccessTo.productSales && (
              <option value="productSales">Product Sales Analysis</option>
            )}
            {hasAccessTo.userSignups && (
              <option value="userSignups">User Signups</option>
            )}
            {hasAccessTo.userEngagements && (
              <option value="userEngagements">User Engagements</option>
            )}
            {hasAccessTo.orders && <option value="orders">Orders</option>}
            {hasAccessTo.vendors && <option value="vendors">Vendors</option>}
          </select>
        </div>
      </div>

      {/* Display access denied message if user doesn't have permission */}
      {!canAccessCurrentDashboard && (
        <Alert
          message="Access Denied"
          description="You don't have permission to view this dashboard. Please contact your administrator if you need access."
          type="error"
          showIcon
          className="mb-4"
        />
      )}

      {/* Display Selected Dashboard */}
      <div
        className="mb-8 relative"
        style={{ height: "calc(100vh - 200px)", minHeight: "500px" }}
      >
        {/* Only render iframe if user has permission */}
        {canAccessCurrentDashboard &&
          Object.entries(dashboardUrls).map(([key, url]) => (
            <div
              key={key}
              className="absolute inset-0 w-full h-full"
              style={{
                display: selectedDashboard === key ? "block" : "none",
                overflow: "hidden",
              }}
            >
              <iframe
                title={`Dashboard_${key}`}
                width="100%"
                height="100%"
                src={url}
                frameBorder="0"
                allowFullScreen={true}
                onLoad={() => handleIframeLoad(key)}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
              ></iframe>
            </div>
          ))}

        {/* Loading Spinner (only show if user has permission) */}
        {isLoading && canAccessCurrentDashboard && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
              <p className="mt-4 text-gray-600">Loading dashboard...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
